/**
 * An enrollment photo, by its id, as a data URL — on whichever host is asking.
 *
 * Rows across the app carry only a photo ID; the image itself is fetched on demand, because
 * inlining photographs into a list of 200 people would cost megabytes to render a column of
 * thumbnails. The fetch goes through the injected AttendanceApi, so the local dashboard reads
 * the node directly and the cloud one streams the same bytes through its tunnel as base64 —
 * and nothing above this has to know which.
 *
 * ─── THE CACHE ───
 * Bounded, and shared across every caller. Paging back to a page you have already seen must
 * not re-download its faces, but an unbounded map over a 500-person directory is tens of
 * megabytes held for the life of the tab. Ids are immutable — a replaced photo is a new row
 * with a new id — so a cached entry can never go stale.
 *
 * A failure (deleted object, proxy error, a backend too old to send the id) resolves to null
 * and the caller shows its placeholder. That is deliberately indistinguishable from "no photo":
 * down a list, a face missing for one reason should not look different from one missing for
 * another, and the operator cannot act on the distinction anyway.
 */
import { useEffect, useState } from 'react';
import { useApi } from '../../api/ApiProvider';

const PHOTO_CACHE = new Map<string, string | null>();
const PHOTO_CACHE_MAX = 150;

export function cacheEnrolledPhoto(id: string, url: string | null) {
  PHOTO_CACHE.set(id, url);
  while (PHOTO_CACHE.size > PHOTO_CACHE_MAX) {
    // Map iterates in insertion order, so the first key is the oldest.
    const oldest = PHOTO_CACHE.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    PHOTO_CACHE.delete(oldest);
  }
}

/** The photo as a data URL, or null while loading, absent, or unfetchable. */
export function useEnrolledPhoto(photoId?: string | null): string | null {
  const { attendance } = useApi();
  const [src, setSrc] = useState<string | null>(
    () => (photoId ? PHOTO_CACHE.get(photoId) ?? null : null),
  );

  useEffect(() => {
    if (!photoId) { setSrc(null); return; }
    if (PHOTO_CACHE.has(photoId)) { setSrc(PHOTO_CACHE.get(photoId) ?? null); return; }
    let alive = true;
    attendance.employeePhotoDataUrl(photoId)
      .then((u) => { cacheEnrolledPhoto(photoId, u); if (alive) setSrc(u); })
      .catch(() => { if (alive) setSrc(null); });
    return () => { alive = false; };
  }, [attendance, photoId]);

  return src;
}
